import { CustomTooltipProps } from './types';
import { createElement, useEffect, useRef, useState } from 'react';
import { Popover } from '../Popover';
import { Flow } from '../Layouts/Flow';
import { setRef } from '../../util/ref-util';
import { postfixTestId } from '../../util/testId';
import cn from './CustomTooltip.css.json';
import './CustomTooltip.css';

export const CustomTooltip = (props: CustomTooltipProps) => {
  const [disableFocusLock, setDisableFocusLock] = useState(false);
  const [mouseOpen, setMouseOpen] = useState(false);
  const referenceElementRef = useRef<HTMLDivElement>(null);
  const [popoverPosition, setPopoverPosition] = useState<DOMRect | undefined>();

  const getReferenceElement: (element?: Element) => Element | undefined = (element?: Element) => {
    if (element) {
      if (window.getComputedStyle(element).display === 'contents') {
        return getReferenceElement(element.children?.[0]);
      } else {
        return element;
      }
    }
  };
  const openTimer = useRef(-1);
  const closeTimer = useRef(-1);

  useEffect(() => {
    if (props.isOpen && !mouseOpen) {
      props.initialFocusRef?.current?.focus();
    }
  }, [mouseOpen, props.initialFocusRef, props.isOpen]);

  return (
    <div
      className={cn.root}
      onMouseLeave={() => {
        if (openTimer.current > 0) clearTimeout(openTimer.current);
        closeTimer.current = window.setTimeout(() => {
          props.onClose();
          setMouseOpen(false);
          closeTimer.current = -1;
        }, 500);
      }}
      onMouseEnter={() => {
        if (closeTimer.current > 0) clearTimeout(closeTimer.current);
      }}
    >
      <div
        className={cn.referenceElementContainer}
        onMouseEnter={() => {
          setMouseOpen(true);
          setDisableFocusLock(true);
          openTimer.current = window.setTimeout(() => {
            openTimer.current = -1;
            (props.title || props.content) && props.onOpen();
          }, 500);
        }}
        onMouseMove={(e) => {
          !props.isOpen &&
            setPopoverPosition({
              bottom: e?.nativeEvent.clientY,
              height: 0,
              left: e?.nativeEvent.clientX,
              right: e?.nativeEvent.clientX,
              top: e?.nativeEvent.clientY,
              width: 0,
            } as DOMRect);
        }}
        onKeyDown={(e) => {
          if (props.isOpen && e.key === 'Escape') {
            setDisableFocusLock(false);
            props.onClose();
            referenceElementRef.current?.focus();
            setMouseOpen(false);
          }
        }}
        onMouseUp={() => {
          if (openTimer.current > 0) clearTimeout(openTimer.current);
          setDisableFocusLock(false);
          props.onClose();
          setMouseOpen(false);
        }}
        data-testid={postfixTestId(props.testId, 'referenceElementContainer')}
        ref={(element) => {
          setRef(referenceElementRef, getReferenceElement(element?.children[0]));
        }}
      >
        {props.children}
      </div>
      <Popover
        className={cn.basePopover}
        title={props.title}
        focusLockProps={{
          disabled: props.focusLockProps?.disabled && disableFocusLock,
          ...props.focusLockProps,
        }}
        isOpen={props.isOpen}
        onClose={() => {
          setDisableFocusLock(true);
          props.onClose();
          setMouseOpen(false);
        }}
        hideArrow={props.hideArrow ?? true}
        placement={props.placement ?? 'auto'}
        referenceElementRef={mouseOpen ? undefined : referenceElementRef}
        focusable
        role="dialog"
        aria-label={props.ariaLabel}
        aria-labelledby={props.ariaLabelledBy}
        aria-describedby={props.ariaDescribedBy}
        position={popoverPosition}
        data-testid={props.testId}
      >
        <Flow orientation="vertical">{props.content}</Flow>
      </Popover>
    </div>
  );
};
